import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Empty,
  Input,
  message,
  Pagination,
  Spin,
} from 'antd';
import React, { useState, useMemo, useEffect, useRef } from 'react';

import { fetchAPI } from '../utils/api';

const emptyResp = {
  items: [],
  page: 1,
  per_page: 10,
  total: 0,
};

const TableFilterAdvanced = (options: {
  endpoint: string;
  method?: string;
  errorMsg?: string;
  searchPlaceholder?: string;
  parser: {
    value: (item: any) => any;
    label: (item: any) => any;
  };
  hideSearch?: boolean;
  hideSelectAll?: boolean;
  apiRespParser?: (resp: any) => any;
  idsParam?: string;
}) => {
  const [filter, setFilter] = useState({ q: '', page: 1 });
  const [selected, setSelected] = useState<string[]>([]);

  const idsParam = options.idsParam || 'ids';

  const FilterDropdownInner = ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }: {
    setSelectedKeys?: (keys: string[]) => void;
    selectedKeys?: string[];
    confirm: () => void;
    clearFilters: () => void;
  }) => {
    const incomingKeys = selectedKeys?.map(String) || [];
    const prevIncomingKeys = useRef<string[]>([]);

    useEffect(() => {
      // Only sync from parent if incomingKeys actually changed
      const currentKeysStr = JSON.stringify(incomingKeys);
      const prevKeysStr = JSON.stringify(prevIncomingKeys.current);

      if (currentKeysStr !== prevKeysStr) {
        prevIncomingKeys.current = incomingKeys;
        if (incomingKeys.length > 0) {
          setSelected(incomingKeys.map(String));
        }
      }
    }, [incomingKeys]);

    const currentSelected = useMemo(
      () => Array.from(new Set([...selected, ...incomingKeys])),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [selected, incomingKeys]
    );

    const selectedIdsKey = useMemo(
      () =>
        currentSelected
          .filter(id => id)
          .sort()
          .join(','),
      [currentSelected]
    );

    const selectedIds = useMemo(
      () =>
        selectedIdsKey
          .split(',')
          .filter(Boolean)
          .map(Number),
      [selectedIdsKey]
    );

    const selectedIdsKeyIsEmpty = !selectedIdsKey || selectedIdsKey === '';

    const shouldFetchSelected =
      !selectedIdsKeyIsEmpty && selectedIds.length > 0;

    const selectedItemsQuery = useQuery(
      ['selected-items', options.endpoint, selectedIdsKey],
      async () => {
        if (!selectedIds.length) {
          return emptyResp;
        }
        try {
          const resp = await fetchAPI(options.endpoint, {
            method: options.method || 'GET',
            payload: {
              [idsParam]: selectedIds,
              per_page: selectedIds.length,
            },
          });
          return options.apiRespParser ? options.apiRespParser(resp) : resp;
        } catch (error) {
          return emptyResp;
        }
      },
      {
        staleTime: 5 * 60 * 1000,
        enabled: shouldFetchSelected,
      }
    );

    const data = useQuery(
      [options.endpoint, filter.q, filter.page],
      async () => {
        try {
          const resp = await fetchAPI(options.endpoint, {
            method: options.method || 'GET',
            payload: {
              q: filter.q,
              page: filter.page,
              per_page: 10,
            },
          });

          if (
            options.apiRespParser &&
            typeof options.apiRespParser === 'function'
          ) {
            return options.apiRespParser(resp);
          }

          return resp;
        } catch (error) {
          message.error(options.errorMsg || 'Failed request');
          return emptyResp;
        }
      },
      {
        staleTime: 10 * 60 * 1000,
        placeholderData: emptyResp,
      }
    );

    const isLoading =
      data.isLoading || (shouldFetchSelected && selectedItemsQuery.isLoading);
    const isEmpty = !data.data?.items?.length;

    return (
      <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()}>
        {!options.hideSearch && (
          <Input.Search
            placeholder={options.searchPlaceholder || 'Search'}
            onSearch={val => {
              setFilter({ q: val, page: 1 });
            }}
            style={{ marginBottom: 8, display: 'block' }}
            loading={data.isLoading}
            disabled={data.isLoading}
            enterButton
            allowClear
          />
        )}
        {!options.hideSelectAll && data.data?.items?.length > 0 && filter.q && (
          <Button
            type="link"
            size="small"
            onClick={() => {
              const currentPageValues = data.data.items.map(item =>
                options.parser.value(item)
              );
              const merged = Array.from(
                new Set([...currentSelected, ...currentPageValues.map(String)])
              );
              setSelectedKeys?.(merged);
              setSelected(merged);
            }}
            style={{ padding: '0 0 8px 0' }}
          >
            Select All (This Page)
          </Button>
        )}
        <Spin spinning={isLoading}>
          {isEmpty ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <ul style={{ padding: 0, margin: '0 0 15px', listStyle: 'none' }}>
              {(() => {
                const currentPageMap = new Map(
                  data.data.items.map(item => [
                    String(options.parser.value(item)),
                    item,
                  ])
                );

                const selectedItemsMap = new Map(
                  (selectedItemsQuery.data?.items || []).map(item => [
                    String(options.parser.value(item)),
                    item,
                  ])
                );

                const mergedItemMap = new Map([
                  ...Array.from(currentPageMap.entries()),
                  ...Array.from(selectedItemsMap.entries()),
                ]);

                const selectedItems = currentSelected.map(value => ({
                  value,
                  item: mergedItemMap.get(value),
                }));

                const unselectedItems = data.data.items.filter(
                  item =>
                    !currentSelected.includes(
                      String(options.parser.value(item))
                    )
                );

                return (
                  <>
                    {selectedItems.length > 0 && (
                      <>
                        {selectedItems.map(({ value, item }) => (
                          <li key={value} style={{ background: '#f5f5f5' }}>
                            <Checkbox
                              checked={true}
                              onChange={() => {
                                const updated = currentSelected.filter(
                                  sel => sel !== value
                                );
                                setSelectedKeys?.(updated);
                                setSelected(updated);
                              }}
                            >
                              {item ? options.parser.label(item) : value}
                            </Checkbox>
                          </li>
                        ))}
                        {unselectedItems.length > 0 && (
                          <li
                            style={{
                              borderTop: '1px solid #d9d9d9',
                              margin: '8px 0',
                            }}
                          />
                        )}
                      </>
                    )}
                    {unselectedItems.map(item => {
                      const itemValue = String(options.parser.value(item));
                      return (
                        <li key={itemValue}>
                          <Checkbox
                            checked={currentSelected.includes(itemValue)}
                            onChange={e => {
                              if (e.target.checked) {
                                const updated = [...currentSelected, itemValue];
                                setSelectedKeys?.(updated);
                                setSelected(updated);
                              } else {
                                const updated = currentSelected.filter(
                                  sel => sel !== itemValue
                                );
                                setSelectedKeys?.(updated);
                                setSelected(updated);
                              }
                            }}
                          >
                            {options.parser.label(item)}
                          </Checkbox>
                        </li>
                      );
                    })}
                  </>
                );
              })()}
            </ul>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Button
              type="link"
              onClick={() => {
                setFilter({ q: '', page: 1 });
                setSelectedKeys?.([]);
                setSelected([]);
                clearFilters();
              }}
            >
              Reset
            </Button>
            <Pagination
              current={filter.page}
              pageSize={data.data?.per_page || 10}
              total={data.data?.total || 0}
              onChange={page => setFilter(f => ({ ...f, page }))}
              showSizeChanger={false}
              size="small"
              style={{ textAlign: 'right' }}
            />
            <Button type="primary" onClick={() => confirm()}>
              Apply
            </Button>
          </div>
        </Spin>
      </div>
    );
  };

  return FilterDropdownInner;
};

export default TableFilterAdvanced;
